import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import { ArrowLeft, MapPin, Calendar, User, AlertTriangle, FileText, Download, Share2, Eye, Clock, Activity, TrendingUp, Users } from 'lucide-react';
import { locationReports } from '../utils/mapData';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
const CaseDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'analysis' | 'documents'>('overview');
  const caseData = locationReports.find(report => report.id === id);
  if (!caseData) {
    return <div className="flex items-center justify-center h-full" data-id="element-132">
        <Card className="p-8 text-center" data-id="element-133">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" data-id="element-134" />
          <h2 className="text-xl font-bold text-white mb-2" data-id="element-135">Case Not Found</h2>
          <p className="text-gray-400 mb-4" data-id="element-136">
            The case you're looking for doesn't exist.
          </p>
          <button onClick={() => navigate('/cases')} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg" data-id="element-137">
            Back to Cases
          </button>
        </Card>
      </div>;
  }
  return <div className="space-y-6" data-id="element-138">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-139">
        <div className="flex items-center space-x-4" data-id="element-140">
          <button onClick={() => navigate('/cases')} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-141">
            <ArrowLeft className="h-5 w-5 text-gray-400" data-id="element-142" />
          </button>
          <div data-id="element-143">
            <div className="flex items-center space-x-3 mb-1" data-id="element-144">
              <h1 className="text-2xl font-bold text-white" data-id="element-145">
                {caseData.title}
              </h1>
              <Badge variant={caseData.riskLevel === 'critical' ? 'red' : caseData.riskLevel === 'high' ? 'orange' : caseData.riskLevel === 'moderate' ? 'yellow' : 'green'} data-id="element-146">
                {caseData.riskLevel.toUpperCase()}
              </Badge>
              <Badge variant="blue" data-id="element-147">{caseData.category}</Badge>
            </div>
            <p className="text-sm text-gray-400" data-id="element-148">Case ID: {caseData.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-149">
          <button className="flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700" data-id="element-150">
            <Download className="h-4 w-4 mr-2" data-id="element-151" />
            Export
          </button>
          <button className="flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700" data-id="element-152">
            <Share2 className="h-4 w-4 mr-2" data-id="element-153" />
            Share
          </button>
          <button className="flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg" data-id="element-154">
            <Eye className="h-4 w-4 mr-2" data-id="element-155" />
            Monitor
          </button>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-156">
        <Card className="p-4" data-id="element-157">
          <div className="flex items-center justify-between" data-id="element-158">
            <div data-id="element-159">
              <p className="text-sm text-gray-400" data-id="element-160">Status</p>
              <p className="text-lg font-semibold text-white mt-1 capitalize" data-id="element-161">
                {caseData.status}
              </p>
            </div>
            <Activity className="h-8 w-8 text-cyan-400" data-id="element-162" />
          </div>
        </Card>
        <Card className="p-4" data-id="element-163">
          <div className="flex items-center justify-between" data-id="element-164">
            <div data-id="element-165">
              <p className="text-sm text-gray-400" data-id="element-166">Deputy</p>
              <p className="text-lg font-semibold text-white mt-1" data-id="element-167">
                {caseData.deputy}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-400" data-id="element-168" />
          </div>
        </Card>
        <Card className="p-4" data-id="element-169">
          <div className="flex items-center justify-between" data-id="element-170">
            <div data-id="element-171">
              <p className="text-sm text-gray-400" data-id="element-172">BINDA Region</p>
              <p className="text-lg font-semibold text-white mt-1" data-id="element-173">
                {caseData.bindaRegion}
              </p>
            </div>
            <MapPin className="h-8 w-8 text-orange-400" data-id="element-174" />
          </div>
        </Card>
        <Card className="p-4" data-id="element-175">
          <div className="flex items-center justify-between" data-id="element-176">
            <div data-id="element-177">
              <p className="text-sm text-gray-400" data-id="element-178">Last Update</p>
              <p className="text-lg font-semibold text-white mt-1" data-id="element-179">
                {caseData.timestamp}
              </p>
            </div>
            <Clock className="h-8 w-8 text-green-400" data-id="element-180" />
          </div>
        </Card>
      </div>
      {/* Tabs */}
      <Card data-id="element-181">
        <div className="border-b border-gray-800" data-id="element-182">
          <div className="flex space-x-1 p-2" data-id="element-183">
            <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`} data-id="element-184">
              Overview
            </button>
            <button onClick={() => setActiveTab('timeline')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'timeline' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`} data-id="element-185">
              Timeline
            </button>
            <button onClick={() => setActiveTab('analysis')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'analysis' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`} data-id="element-186">
              AI Analysis
            </button>
            <button onClick={() => setActiveTab('documents')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'documents' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`} data-id="element-187">
              Documents
            </button>
          </div>
        </div>
        <div className="p-6" data-id="element-188">
          {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-189">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6" data-id="element-190">
                {/* AI Summary */}
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-800/30 rounded-lg p-4" data-id="element-191">
                  <div className="flex items-center mb-3" data-id="element-192">
                    <div className="w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse" data-id="element-193"></div>
                    <span className="text-sm font-semibold text-cyan-400 uppercase" data-id="element-194">
                      AI Intelligence Summary
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed" data-id="element-195">
                    {caseData.aiSummary}
                  </p>
                </div>
                {/* Description */}
                <div data-id="element-196">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-197">
                    Case Description
                  </h3>
                  <p className="text-gray-300 leading-relaxed" data-id="element-198">
                    {caseData.description}
                  </p>
                </div>
                {/* Affected Area */}
                <div data-id="element-199">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-200">
                    Affected Area
                  </h3>
                  <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-201">
                    <div className="flex items-start" data-id="element-202">
                      <MapPin className="h-5 w-5 text-orange-400 mr-3 mt-0.5" data-id="element-203" />
                      <div data-id="element-204">
                        <p className="text-white font-medium" data-id="element-205">
                          {caseData.location}
                        </p>
                        <p className="text-gray-400 text-sm mt-1" data-id="element-206">
                          {caseData.affectedArea}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Impact Assessment */}
                <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-4" data-id="element-207">
                  <div className="flex items-center mb-3" data-id="element-208">
                    <TrendingUp className="h-5 w-5 text-orange-400 mr-2" data-id="element-209" />
                    <h3 className="text-lg font-semibold text-orange-400" data-id="element-210">
                      Estimated Impact
                    </h3>
                  </div>
                  <p className="text-gray-300" data-id="element-211">{caseData.estimatedImpact}</p>
                </div>
                {/* Recommendations */}
                <div data-id="element-212">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-213">
                    Recommended Actions
                  </h3>
                  <ul className="space-y-3" data-id="element-214">
                    {caseData.recommendations.map((rec, index) => <li key={index} className="flex items-start bg-gray-800/50 rounded-lg p-3" data-id="element-215">
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cyan-600 text-xs text-white mr-3 mt-0.5 flex-shrink-0" data-id="element-216">
                          {index + 1}
                        </span>
                        <span className="text-gray-300" data-id="element-217">{rec}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              {/* Sidebar */}
              <div className="space-y-6" data-id="element-218">
                {/* Map */}
                <div data-id="element-219">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-220">
                    Location
                  </h3>
                  <div className="h-[250px] rounded-lg overflow-hidden border border-gray-800" data-id="element-221">
                    <MapContainer center={caseData.coordinates} zoom={10} style={{
                  height: '100%',
                  width: '100%'
                }} className="z-0" data-id="element-222">
                      <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-223" />
                      <Marker position={caseData.coordinates} data-id="element-224" />
                    </MapContainer>
                  </div>
                </div>
                {/* Intelligence Sources */}
                <div data-id="element-225">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-226">
                    Intelligence Sources
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-227">
                    {caseData.sources.map(source => <span key={source} className="px-3 py-1.5 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-228">
                        {source}
                      </span>)}
                  </div>
                </div>
                {/* Related Cases */}
                {caseData.relatedCases.length > 0 && <div data-id="element-229">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-230">
                      Related Cases
                    </h3>
                    <div className="space-y-2" data-id="element-231">
                      {caseData.relatedCases.map(caseId => <button key={caseId} onClick={() => navigate(`/case/${caseId}`)} className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors" data-id="element-232">
                          <span className="text-white" data-id="element-233">{caseId}</span>
                          <ArrowLeft className="h-4 w-4 text-gray-400 rotate-180" data-id="element-234" />
                        </button>)}
                    </div>
                  </div>}
                {/* Case Team */}
                <div data-id="element-235">
                  <h3 className="text-lg font-semibold text-white mb-3" data-id="element-236">
                    Case Team
                  </h3>
                  <div className="space-y-2" data-id="element-237">
                    <div className="flex items-center p-3 bg-gray-800/50 rounded-lg" data-id="element-238">
                      <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center text-white font-semibold mr-3" data-id="element-239">
                        D
                      </div>
                      <div data-id="element-240">
                        <p className="text-white font-medium" data-id="element-241">
                          {caseData.deputy}
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-242">Lead Deputy</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-gray-800/50 rounded-lg" data-id="element-243">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold mr-3" data-id="element-244">
                        B
                      </div>
                      <div data-id="element-245">
                        <p className="text-white font-medium" data-id="element-246">
                          {caseData.bindaRegion}
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-247">
                          Regional Intelligence
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'timeline' && <div className="space-y-4" data-id="element-248">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-249">
                Case Timeline
              </h3>
              <div className="relative" data-id="element-250">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-800" data-id="element-251"></div>
                <div className="space-y-6 relative" data-id="element-252">
                  <div className="flex items-start" data-id="element-253">
                    <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center text-white text-xs font-semibold mr-4 relative z-10" data-id="element-254">
                      1
                    </div>
                    <div className="flex-1 bg-gray-800/50 rounded-lg p-4" data-id="element-255">
                      <div className="flex items-center justify-between mb-2" data-id="element-256">
                        <span className="text-white font-medium" data-id="element-257">
                          Case Initiated
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-258">
                          {caseData.timestamp}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400" data-id="element-259">
                        Initial intelligence report received and case opened
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-260">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold mr-4 relative z-10" data-id="element-261">
                      2
                    </div>
                    <div className="flex-1 bg-gray-800/50 rounded-lg p-4" data-id="element-262">
                      <div className="flex items-center justify-between mb-2" data-id="element-263">
                        <span className="text-white font-medium" data-id="element-264">
                          Investigation Started
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-265">
                          2 hours ago
                        </span>
                      </div>
                      <p className="text-sm text-gray-400" data-id="element-266">
                        Field team deployed for on-ground verification
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-267">
                    <div className="w-8 h-8 rounded-full bg-yellow-600 flex items-center justify-center text-white text-xs font-semibold mr-4 relative z-10" data-id="element-268">
                      3
                    </div>
                    <div className="flex-1 bg-gray-800/50 rounded-lg p-4" data-id="element-269">
                      <div className="flex items-center justify-between mb-2" data-id="element-270">
                        <span className="text-white font-medium" data-id="element-271">
                          Status Update
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-272">
                          30 minutes ago
                        </span>
                      </div>
                      <p className="text-sm text-gray-400" data-id="element-273">
                        Additional intelligence sources confirmed initial
                        findings
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'analysis' && <div className="space-y-6" data-id="element-274">
              <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-800/30 rounded-lg p-6" data-id="element-275">
                <div className="flex items-center mb-4" data-id="element-276">
                  <TrendingUp className="h-6 w-6 text-purple-400 mr-3" data-id="element-277" />
                  <h3 className="text-xl font-semibold text-white" data-id="element-278">
                    AI-Powered Analysis
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-279">
                  <div data-id="element-280">
                    <h4 className="text-sm font-semibold text-purple-400 mb-2" data-id="element-281">
                      Threat Assessment
                    </h4>
                    <p className="text-gray-300" data-id="element-282">{caseData.aiSummary}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-283">
                    <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-284">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-285">
                        Confidence Level
                      </p>
                      <div className="flex items-center" data-id="element-286">
                        <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden mr-3" data-id="element-287">
                          <div className="h-full bg-purple-500" style={{
                        width: '87%'
                      }} data-id="element-288"></div>
                        </div>
                        <span className="text-white font-semibold" data-id="element-289">87%</span>
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-290">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-291">
                        Urgency Score
                      </p>
                      <div className="flex items-center" data-id="element-292">
                        <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden mr-3" data-id="element-293">
                          <div className="h-full bg-orange-500" style={{
                        width: '92%'
                      }} data-id="element-294"></div>
                        </div>
                        <span className="text-white font-semibold" data-id="element-295">92%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'documents' && <div className="space-y-4" data-id="element-296">
              <div className="flex items-center justify-between mb-4" data-id="element-297">
                <h3 className="text-lg font-semibold text-white" data-id="element-298">
                  Case Documents
                </h3>
                <button className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm" data-id="element-299">
                  Upload Document
                </button>
              </div>
              <div className="space-y-3" data-id="element-300">
                <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer" data-id="element-301">
                  <div className="flex items-center" data-id="element-302">
                    <FileText className="h-5 w-5 text-cyan-400 mr-3" data-id="element-303" />
                    <div data-id="element-304">
                      <p className="text-white font-medium" data-id="element-305">
                        Initial Intelligence Report
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-306">
                        PDF • 2.4 MB • {caseData.timestamp}
                      </p>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-gray-600 rounded" data-id="element-307">
                    <Download className="h-4 w-4 text-gray-400" data-id="element-308" />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer" data-id="element-309">
                  <div className="flex items-center" data-id="element-310">
                    <FileText className="h-5 w-5 text-blue-400 mr-3" data-id="element-311" />
                    <div data-id="element-312">
                      <p className="text-white font-medium" data-id="element-313">
                        Field Investigation Report
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-314">
                        PDF • 1.8 MB • 2 hours ago
                      </p>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-gray-600 rounded" data-id="element-315">
                    <Download className="h-4 w-4 text-gray-400" data-id="element-316" />
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </Card>
    </div>;
};
export default CaseDetail;